/**
 * Résolution du tenant au boot d'une PWA.
 *
 * Ordre de priorité :
 *   1. Sous-domaine de l'hôte      → `<tenant>.apps.alterrego.com` (ou alias `domains`)
 *   2. `import.meta.env.VITE_TENANT_ID` (tenant figé au build)
 *   3. `fallback` explicite passé par l'app
 *   4. Fallback ultime `'sauf-imprevu'` (tenant historique par défaut)
 *
 * Fonction pure : hostname + env sont injectables pour les tests.
 */
import { domainMap, registry } from './registry';

/** Tenant par défaut historique. Utilisé si toute autre résolution échoue. */
export const DEFAULT_TENANT = 'sauf-imprevu';

export interface ResolveOptions {
  /** Hôte à analyser. Défaut : `window.location.hostname`. */
  hostname?: string;
  /** Objet env façon `import.meta.env`. Défaut : indéfini. */
  env?: Record<string, unknown>;
  /** Tenant de repli propre à l'app (avant le fallback ultime). */
  fallback?: string;
  /** Liste des tenants connus. Défaut : clés du registry. */
  knownTenants?: string[];
}

/**
 * Domaines de base sur lesquels un sous-domaine `<tenant>.<base>` porte
 * directement l'id (ou un alias) du tenant.
 *   - `apps.alterrego.com` : domaine de prod actuel (wildcard DNS + Caddy).
 *   - `boostore.com`       : conservé en backward-compat (docs/ancien pattern).
 */
const BASE_DOMAINS = ['apps.alterrego.com', 'boostore.com'];

/**
 * Extrait un tenant depuis un hostname.
 * Gère `<sub>.<base>` (bases connues), les alias déclarés (`domainMap`), et un
 * match direct sur l'`id` tenant via le premier label. Renvoie `null` pour
 * localhost / IP / host inconnu.
 *
 * Patterns supportés (backward compat) :
 *   - `mayday.apps.alterrego.com`         → `mayday` (sous-domaine direct)
 *   - `mockup-mayday.apps.alterrego.com`  → `mayday` (préfixe de stage `mockup-` retiré)
 *   - `app-mayday.apps.alterrego.com`     → `mayday` (préfixe de stage `app-` retiré)
 *   - `bocuse-dev.apps.alterrego.com`     → alias host complet (`domainMap`)
 *   - `<tenant>.boostore.com`             → historique
 */
export function tenantFromHostname(hostname: string, known: string[]): string | null {
  if (!hostname) return null;
  const host = hostname.toLowerCase().replace(/\.$/, '');

  // Host complet mappé explicitement (ex. bocuse-dev.apps.alterrego.com).
  if (domainMap[host]) return domainMap[host];

  // <sub>.<base> pour chaque domaine de base connu (le sous-domaine porte le tenant).
  for (const base of BASE_DOMAINS) {
    const suffix = `.${base}`;
    if (host.endsWith(suffix)) {
      const sub = host.slice(0, -suffix.length).split('.').pop() ?? '';
      if (domainMap[sub]) return domainMap[sub];
      if (known.includes(sub)) return sub;
      // Préfixes de stage (`mockup-<tenant>`, `app-<tenant>`) : on retire le
      // préfixe et on ré-essaie. Rend le pattern `mockup-mayday` documenté
      // effectif pour tous les tenants (branding correct sur le sous-domaine
      // maquette), sans devoir déclarer chaque alias dans `domains[]`.
      const stripped = sub.replace(/^(?:mockup|app)-/, '');
      if (stripped !== sub) {
        if (domainMap[stripped]) return domainMap[stripped];
        if (known.includes(stripped)) return stripped;
      }
    }
  }

  // Premier label du host mappé/connu (ex. app-sauf-imprevu.*).
  const firstLabel = host.split('.')[0];
  if (domainMap[firstLabel]) return domainMap[firstLabel];
  if (known.includes(firstLabel)) return firstLabel;

  return null;
}

/** Résout l'id du tenant courant selon l'ordre de priorité documenté. */
export function resolveTenant(opts: ResolveOptions = {}): string {
  const hostname =
    opts.hostname ??
    (typeof window !== 'undefined' ? window.location.hostname : '');
  const known = opts.knownTenants ?? Object.keys(registry);

  // 1. Sous-domaine
  const fromHost = tenantFromHostname(hostname ?? '', known);
  if (fromHost) return fromHost;

  // 2. VITE_TENANT_ID (build)
  const envTenant = opts.env?.VITE_TENANT_ID;
  if (typeof envTenant === 'string' && envTenant.length > 0) return envTenant;

  // 3. Fallback propre à l'app
  if (opts.fallback) return opts.fallback;

  // 4. Fallback ultime
  return DEFAULT_TENANT;
}
